import type { CustomBaseDataType } from '../interface';
import type { IModelSource } from '../interface/db';
import type IBeaconDto from './BeaconDto.if';
import type IBeaconSensitivityDto from './BeaconSensitivityDto.if';
import type ILanguageDto from './LanguageDto.if';
import type IMediumDto from './MediumDto.if';
import type ISiteDto from './SiteDto.if';
import type ISiteObjectDto_Api from './SiteObjectDto.api';
import type ISiteObjectDto from './SiteObjectDto.if';
import type ISiteObjectTranslationDto from './SiteObjectTranslationDto.if';
import type ISiteObjectTypeDto from './SiteObjectTypeDto.if';
import type IStyleDto from './StyleDto.if';

export interface ISiteObjectDto_Linkables extends CustomBaseDataType {
    site: (database: IModelSource) => Promise<ISiteDto>;
    type: (database: IModelSource) => Promise<ISiteObjectTypeDto>;
    beacon: (database: IModelSource) => Promise<IBeaconDto>;
    beaconSensitivity: (database: IModelSource) => Promise<IBeaconSensitivityDto>;
    nextObject: (database: IModelSource) => Promise<ISiteObjectDto>;
    relatedObject1: (database: IModelSource) => Promise<ISiteObjectDto>;
    relatedObject2: (database: IModelSource) => Promise<ISiteObjectDto>;
    relatedObject3: (database: IModelSource) => Promise<ISiteObjectDto>;
    relatedObject4: (database: IModelSource) => Promise<ISiteObjectDto>;
    relatedObject5: (database: IModelSource) => Promise<ISiteObjectDto>;
    foregroundImageMedium: (database: IModelSource) => Promise<IMediumDto>;
    backgroundImageMedium: (database: IModelSource) => Promise<IMediumDto>;
    menuImageMedium: (database: IModelSource) => Promise<IMediumDto>;
    style: (database: IModelSource) => Promise<IStyleDto>;
    translations: (database: IModelSource) => Promise<ISiteObjectTranslationDto[]>;
    translation: (
        database: IModelSource,
        language: ILanguageDto | number,
    ) => Promise<ISiteObjectTranslationDto>;
    relatedObjects: (database: IModelSource) => Promise<ISiteObjectDto[]>;
}

function _site(this: Partial<ISiteObjectDto_Api>, database: IModelSource): Promise<ISiteDto> {
    return this.siteId
        ? database.get('ISiteDto', this.siteId)
        : Promise.reject('Missing field siteObject.siteId');
}

function _type(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectTypeDto> {
    return this.siteObjectTypeId
        ? database.get('ISiteObjectTypeDto', this.siteObjectTypeId)
        : Promise.reject('Missing field siteObject.siteObjectTypeId');
}

function _beacon(this: Partial<ISiteObjectDto_Api>, database: IModelSource): Promise<IBeaconDto> {
    return this.beaconId
        ? database.get('IBeaconDto', this.beaconId)
        : Promise.reject('Missing field siteObject.beaconId');
}

function _beaconSensitivity(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<IBeaconSensitivityDto> {
    return this.beaconSensitivityId
        ? database.get('IBeaconSensitivityDto', this.beaconSensitivityId)
        : Promise.reject('Missing field siteObject.beaconSensitivityId');
}

function _nextObject(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto> {
    return this.nextObjectId
        ? database.get('ISiteObjectDto', this.nextObjectId)
        : Promise.reject('Missing field siteObject.nextObjectId');
}

function _relatedObject1(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto> {
    return this.relatedObject1Id
        ? database.get('ISiteObjectDto', this.relatedObject1Id)
        : Promise.reject('Missing field siteObject.relatedObject1Id');
}

function _relatedObject2(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto> {
    return this.relatedObject2Id
        ? database.get('ISiteObjectDto', this.relatedObject2Id)
        : Promise.reject('Missing field siteObject.relatedObject2Id');
}

function _relatedObject3(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto> {
    return this.relatedObject3Id
        ? database.get('ISiteObjectDto', this.relatedObject3Id)
        : Promise.reject('Missing field siteObject.relatedObject3Id');
}

function _relatedObject4(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto> {
    return this.relatedObject4Id
        ? database.get('ISiteObjectDto', this.relatedObject4Id)
        : Promise.reject('Missing field siteObject.relatedObject4Id');
}

function _relatedObject5(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto> {
    return this.relatedObject5Id
        ? database.get('ISiteObjectDto', this.relatedObject5Id)
        : Promise.reject('Missing field siteObject.relatedObject5Id');
}

function _foregroundImageMedium(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<IMediumDto> {
    return this.foregroundImageMediumId
        ? database.get('IMediumDto', this.foregroundImageMediumId)
        : Promise.reject('Missing field siteObject.foregroundImageMediumId');
}

function _backgroundImageMedium(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<IMediumDto> {
    return this.backgroundImageMediumId
        ? database.get('IMediumDto', this.backgroundImageMediumId)
        : Promise.reject('Missing field siteObject.backgroundImageMediumId');
}

function _menuImageMedium(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<IMediumDto> {
    return this.menuImageMediumId
        ? database.get('IMediumDto', this.menuImageMediumId)
        : Promise.reject('Missing field siteObject.menuImageMediumId');
}

function _style(this: Partial<ISiteObjectDto_Api>, database: IModelSource): Promise<IStyleDto> {
    return this.styleId
        ? database.get('IStyleDto', this.styleId)
        : Promise.reject('Missing field siteObject.styleId');
}

async function _translations(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectTranslationDto[]> {
    if (!this.id) {
        return Promise.reject('Missing field siteObject.id');
    }

    const siteObjectId = this.id;

    const results = await database.all('ISiteObjectTranslationDto');
    const matches = results.filter(item => item.siteObjectId === siteObjectId);

    if (matches.length === 0) {
        throw new Error(`No translations found for siteObjectId:${siteObjectId}`);
    }

    return matches;
}

async function _translation(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
    language: ILanguageDto | number,
): Promise<ISiteObjectTranslationDto> {
    if (!this.id) {
        return Promise.reject(new Error('Missing field siteObject.id'));
    }

    const languageId: number =
        (language as ILanguageDto).id !== undefined
            ? Number((language as ILanguageDto).id ?? -1)
            : Number(language);

    const allTranslations = await _translations.call(this, database);

    const match = allTranslations.filter(x => x.languageId === languageId);

    const primaryMatch = match.at(0);

    if (!primaryMatch) {
        throw new Error(
            `Unable to find matching translation in siteobject:${this.id}, for language:${languageId}`,
        );
    }

    return primaryMatch;
}

async function _relatedObjects(
    this: Partial<ISiteObjectDto_Api>,
    database: IModelSource,
): Promise<ISiteObjectDto[]> {
    const promises: Promise<ISiteObjectDto>[] = [];

    if (this.relatedObject1Id) {
        promises.push(_relatedObject1.call(this, database));
    }

    if (this.relatedObject2Id) {
        promises.push(_relatedObject2.call(this, database));
    }

    if (this.relatedObject3Id) {
        promises.push(_relatedObject3.call(this, database));
    }

    if (this.relatedObject4Id) {
        promises.push(_relatedObject4.call(this, database));
    }

    if (this.relatedObject5Id) {
        promises.push(_relatedObject5.call(this, database));
    }

    const objects: ISiteObjectDto[] = await Promise.all(promises);

    return objects;
}

/** 
 * @description all custom properties for
   SiteObjectDto & methods to link against other entities
*/
function linkables(model: Partial<ISiteObjectDto_Api>): ISiteObjectDto_Linkables {
    return {
        site: _site.bind(model),
        type: _type.bind(model),
        beacon: _beacon.bind(model),
        beaconSensitivity: _beaconSensitivity.bind(model),
        nextObject: _nextObject.bind(model),
        relatedObject1: _relatedObject1.bind(model),
        relatedObject2: _relatedObject2.bind(model),
        relatedObject3: _relatedObject3.bind(model),
        relatedObject4: _relatedObject4.bind(model),
        relatedObject5: _relatedObject5.bind(model),
        foregroundImageMedium: _foregroundImageMedium.bind(model),
        backgroundImageMedium: _backgroundImageMedium.bind(model),
        menuImageMedium: _menuImageMedium.bind(model),
        style: _style.bind(model),
        translations: _translations.bind(model),
        translation: _translation.bind(model),
        relatedObjects: _relatedObjects.bind(model),
    };
}

export default linkables;
